"use client";

import Image from "next/image";
import * as React from "react";
import { cn } from "@/lib/utils";

export interface LogoProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Se true, exibe apenas o ícone/marca do logo sem o texto.
   * Se false (padrão), exibe o ícone e o texto lado a lado.
   */
  condensed?: boolean;
  /**
   * Subtítulo / Descrição exibido abaixo do logotipo.
   * Padrão: "NFC Tag & QRCodes Dinamicos".
   * Pode ser sobrescrito ou omitido passando null ou string vazia.
   */
  subtitle?: string | null;
  /**
   * Sufixo opcional exibido ao final de "TagFull" (ex: "Pet", "Review").
   */
  suffix?: string;
  /**
   * Classes extras para estilizar o sufixo (ex: cor personalizada).
   */
  suffixClassName?: string;
  /**
   * Tema de cores do ícone SVG e do wordmark ("default" | "pet" | "review").
   */
  theme?: "default" | "pet" | "review";
  /**
   * Tamanho do componente do logo.
   * sm, md (padrão), lg
   */
  size?: "sm" | "md" | "lg";
  /**
   * Estilo visual (compatibilidade).
   */
  variant?: "solid" | "subtle";
  /**
   * Quando true e condensed é false, oculta automaticamente o texto quando
   * estiver dentro de uma Sidebar colapsada do Shadcn (group-data-[collapsible=icon]:hidden).
   */
  responsiveCollapse?: boolean;
}

export function Logo({
  condensed = false,
  subtitle = "NFC Tag & QRCodes Dinamicos",
  suffix,
  suffixClassName,
  theme = "default",
  size = "md",
  variant = "solid",
  responsiveCollapse = true,
  className,
  ...props
}: LogoProps) {
  const logoSrc =
    theme === "pet"
      ? "/logos/logo-pet.svg"
      : theme === "review"
        ? "/logos/logo-review.svg"
        : "/logos/logo-condensed-final-v2.svg";

  const fullColorClass =
    theme === "pet"
      ? "text-orange-500 dark:text-orange-400"
      : theme === "review"
        ? "text-[#4285F4] dark:text-[#669DF6]"
        : "text-[#0192f2] dark:text-[#02b0fa]";

  const defaultSuffixColorClass =
    theme === "pet"
      ? "text-amber-500 dark:text-amber-400"
      : theme === "review"
        ? "text-[#34A853] dark:text-[#46BD67]"
        : "text-orange-500";
  const iconSizes = {
    sm: "h-7 w-auto",
    md: "h-8.5 w-auto",
    lg: "h-10 w-auto",
  };

  const titleSizes = {
    sm: "text-base tracking-tight",
    md: "text-lg tracking-tight",
    lg: "text-xl tracking-tight",
  };

  const subtitleSizes = {
    sm: "text-[8.5px]",
    md: "text-[9.5px]",
    lg: "text-[11px]",
  };

  // Estado apenas do logo (somente o ícone da tag)
  if (condensed) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center transition-transform",
          className,
        )}
        title="TagFull - NFC Tag & QRCodes Dinamicos"
        aria-label="TagFull Logo"
        {...props}
      >
        <Image
          src={logoSrc}
          alt="TagFull"
          width={48}
          height={32}
          className={cn(
            "object-contain shrink-0 drop-shadow-xs",
            iconSizes[size],
          )}
          priority
        />
      </div>
    );
  }

  // Estado completo (ícone + texto "tagfull" + descrição)
  return (
    <div
      className={cn(
        "flex items-center gap-2 overflow-hidden select-none",
        responsiveCollapse &&
          "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0",
        className,
      )}
      {...props}
    >
      <div className="flex shrink-0 items-center justify-center">
        <Image
          src={logoSrc}
          alt="TagFull"
          width={52}
          height={35}
          className={cn(
            "object-contain shrink-0 drop-shadow-xs",
            iconSizes[size],
          )}
          priority
        />
      </div>

      <div
        className={cn(
          "flex flex-col overflow-hidden text-left leading-none",
          responsiveCollapse && "group-data-[collapsible=icon]:hidden",
        )}
      >
        {/* Wordmark tagfull */}
        <span
          className={cn(
            "font-black tracking-tighter flex items-center leading-none",
            titleSizes[size],
          )}
        >
          <span className="text-[#0c1829] dark:text-white">Tag</span>
          <span className={fullColorClass}>Full</span>
          {suffix && (
            <span
              className={cn("ml-1", defaultSuffixColorClass, suffixClassName)}
            >
              {suffix}
            </span>
          )}
        </span>

        {/* Subtítulo / Descrição */}
        {subtitle && (
          <span
            className={cn(
              "text-muted-foreground truncate font-semibold tracking-normal mt-0 leading-none",
              subtitleSizes[size],
            )}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}
